import React from 'react';
import { useSelector } from 'react-redux';
import { NamePrefixModifier, withInjectedProps } from '../../../../../../Core/InjectedPropsContext';
import { WorksheetAssociatedTestSection } from '../../../../../Core';
import { IWorksheetComponent } from '../../../../../Core/types';
import { Phase } from '../../../../types';

import { RemoveSeparationButton } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Components';
import {
  createWashingSieveSizeCaptionSelector,
  getPhaseFollowingSeparation,
  getSeparationPath,
  isPhaseEarlierThan,
} from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { createSieveSizeCaptionGetter } from 'Data/ViewModels/Equipment/ISieveSize';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { Field, NumericInput, TextOutput } from '../../../../../../Core';
import { ListFieldSection } from '../../../../../../Layout';
import { WorksheetBalanceFieldSet } from '../../../../../Core/WorksheetEquipment';

type ISeparationSectionProps = IWorksheetComponent & {
  index: number;
};

let SeparationSection: React.FC<ISeparationSectionProps> = ({ createWorksheetSelector, index }) => {
  const dryingPhase = useSelector(createWorksheetSelector!('Preparation.DryingPhase')) as Phase;
  const separationPath = getSeparationPath(index);

  const phaseFollowingSeparation = getPhaseFollowingSeparation(index);

  const isDriedBeforeSeparation =
    isPhaseEarlierThan(dryingPhase, phaseFollowingSeparation) || dryingPhase === Phase.Never;
  const isDriedAfterSeparation = dryingPhase === phaseFollowingSeparation;

  const isWholePortionWashed = useSelector(
    pipe(createWorksheetSelector!('WholePortion.Washing.IsWashed'), valueOrDefault(false))
  );

  const isFinePortionSubspecimenWashed = useSelector(
    pipe(createWorksheetSelector!(`${separationPath}.FinePortion.Subspecimen.Washing.IsWashed`), valueOrDefault(false))
  );

  const separatingSieveSizeId = useSelector(createWorksheetSelector!(`${separationPath}.SeparatingSieveSizeId`));

  const washingSieveSizeCaption = useSelector(createWashingSieveSizeCaptionSelector(createWorksheetSelector!));

  const separatingSieveSizeCaption = useSelector(
    pipe(
      createWorksheetSelector!('Equipment.AllSieveSizes'),
      createSieveSizeCaptionGetter(separatingSieveSizeId),
      valueOrDefault('N/A')
    )
  );

  return (
    <>
      <ListFieldSection>
        <Field input={<TextOutput />} label="Separating Sieve Size" value={separatingSieveSizeCaption} />
      </ListFieldSection>
      <ListFieldSection heading="Coarse Portion Mass">
        {index === 0 && (
          <>
            <Field
              input={<TextOutput />}
              label="Specimen Total Mass"
              name="WholePortion.Masses.WetMass"
              visible={!isDriedBeforeSeparation}
            />
            <Field
              input={<TextOutput />}
              label="Specimen Total Dry Mass"
              name="WholePortion.Masses.DryMass"
              visible={isDriedBeforeSeparation}
            />
            <Field
              input={<TextOutput />}
              label="Specimen Total Washed Dry Mass"
              name="WholePortion.Washing.WashedDryMass"
              visible={isWholePortionWashed}
            />
          </>
        )}
        <NamePrefixModifier appendNamePrefix={separationPath}>
          <Field input={<TextOutput />} label="Coarse Portion Dry Mass" name="CoarsePortion.Masses.DryMass" />
          <Field
            input={<TextOutput />}
            label="Fine Portion Mass"
            name="FinePortion.Masses.WetMass"
            visible={!isDriedBeforeSeparation}
          />
          <Field
            input={<TextOutput />}
            label="Fine Portion Dry Mass"
            name="FinePortion.Masses.DryMass"
            visible={!isDriedAfterSeparation}
          />
        </NamePrefixModifier>
      </ListFieldSection>
      {isDriedAfterSeparation && (
        <>
          <NamePrefixModifier appendNamePrefix={separationPath}>
            <WorksheetAssociatedTestSection
              heading={`Fine Portion Subspecimen Mass and Water Content`}
              testType="Moisture Content"
              associatedTestModel={{
                measured: true,
              }}
              name="FinePortion.Subspecimen.Masses.AssociatedMoistureContent"
            />
          </NamePrefixModifier>
          <ListFieldSection>
            <NamePrefixModifier appendNamePrefix={separationPath}>
              <Field input={<TextOutput />} label="Fine Portion Dry Mass" name="FinePortion.Masses.DryMass" />
            </NamePrefixModifier>
            <Field input={<TextOutput />} label="Specimen Total Dry Mass" name="WholePortion.Masses.DryMass" />
          </ListFieldSection>
        </>
      )}
      {!isDriedAfterSeparation && (
        <ListFieldSection heading="Fine Portion Subspecimen Mass">
          <NamePrefixModifier appendNamePrefix={separationPath}>
            <NamePrefixModifier appendNamePrefix="FinePortion.Subspecimen.Masses">
              <NamePrefixModifier appendNamePrefix="MassMeasurement">
                <WorksheetBalanceFieldSet
                  label="Balance"
                  name="Equipment.Balance"
                  copyTo={['Equipment.DryMassBalance']}
                />
                <Field input={<NumericInput />} name="TareMass" label="Tare Mass" />
                <Field
                  input={<NumericInput />}
                  name="TareAndWetMass"
                  label={`Tare and Subspecimen Mass`}
                  visible={!isDriedBeforeSeparation}
                />
                <Field
                  input={<TextOutput />}
                  name="WetMass"
                  label={`Subspecimen Mass`}
                  visible={!isDriedBeforeSeparation}
                />
                <Field
                  input={<NumericInput />}
                  name="TareAndDryMass"
                  label={`Tare and Subspecimen Dry Mass`}
                  visible={isDriedBeforeSeparation}
                />
              </NamePrefixModifier>
              <Field input={<TextOutput />} name="DryMass" label={`Subspecimen Dry Mass`} />
            </NamePrefixModifier>
          </NamePrefixModifier>
        </ListFieldSection>
      )}
      {isFinePortionSubspecimenWashed && (
        <>
          <NamePrefixModifier appendNamePrefix={separationPath}>
            <WorksheetAssociatedTestSection
              testType="Wash"
              associatedTestModel={{
                measured: true,
              }}
              name="FinePortion.Subspecimen.Washing.AssociatedWashTest"
            />
          </NamePrefixModifier>
          <ListFieldSection>
            <Field
              input={<TextOutput />}
              label={`Passing ${washingSieveSizeCaption} From Wash (%)`}
              name="Results.PercentagePassingWashingSieve"
            />
          </ListFieldSection>
        </>
      )}
      <NamePrefixModifier appendNamePrefix={separationPath}>
        <div>
          <RemoveSeparationButton />
        </div>
      </NamePrefixModifier>
    </>
  );
};

SeparationSection = withInjectedProps(SeparationSection);

export { SeparationSection };
export default SeparationSection;
